import { useEffect, useState, useMemo, type CSSProperties, type FormEvent } from "react";
import {
  listTrails,
  saveTrail,
  deleteTrail,
  listTrailStops,
  setTrailStops,
  findDistilleryByName,
  type Trail,
  type TrailStop,
} from "../../lib/api";
import GuestStudioTabs from "../../components/admin/GuestStudioTabs";

function slugify(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

const REGION_LABELS: Record<string, string> = {
  kc_local: "Kansas City Local",
  missouri: "Missouri Spirits",
  regional: "Midwest Regional",
  national: "National Heritage",
  international: "World Whiskies",
};

export default function AdminTrails() {
  const [trails, setTrails] = useState<Trail[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [regionFilter, setRegionFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Editor State
  const [editingTrail, setEditingTrail] = useState<Partial<Trail> | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [savingTrail, setSavingTrail] = useState(false);

  // Stops State
  const [stops, setStops] = useState<TrailStop[]>([]);
  const [loadingStops, setLoadingStops] = useState(false);
  const [newDistilleryName, setNewDistilleryName] = useState("");
  const [newStopNotes, setNewStopNotes] = useState("");

  // Toast
  const [toast, setToast] = useState<{ text: string; type: "ok" | "err" } | null>(null);
  const showToast = (text: string, type: "ok" | "err" = "ok") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Escape key closes modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isModalOpen) setIsModalOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await listTrails();
      setTrails(data || []);
    } catch (err: any) {
      showToast(err.message || "Failed to load trails", "err");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredTrails = useMemo(() => {
    let list = trails;
    if (regionFilter !== "all") {
      list = list.filter((t) => (t.region || "").toLowerCase() === regionFilter);
    }
    if (statusFilter !== "all") {
      list = list.filter((t) => (t.status || "").toLowerCase() === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          (t.description && t.description.toLowerCase().includes(q)) ||
          t.slug.toLowerCase().includes(q)
      );
    }
    return list;
  }, [trails, regionFilter, statusFilter, searchQuery]);

  const handleNewTrail = () => {
    setEditingTrail({
      name: "",
      slug: "",
      description: "",
      region: "kc_local",
      duration: "1–2 Days",
      difficulty: "easy",
      status: "draft",
      c33_exclusive: false,
    });
    setStops([]);
    setIsModalOpen(true);
  };

  const handleEditTrail = async (t: Trail) => {
    setEditingTrail({ ...t });
    // Clear the previous trail's stops immediately so a slow fetch can never
    // leave another trail's list in state (set_stops would write it here).
    setStops([]);
    setIsModalOpen(true);
    if (t.id) {
      setLoadingStops(true);
      try {
        const stopRows = await listTrailStops(t.id);
        setStops(stopRows || []);
      } catch (err) {
        setStops([]);
      } finally {
        setLoadingStops(false);
      }
    }
  };

  const handleSaveTrail = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingTrail || !editingTrail.name) return;
    setSavingTrail(true);
    try {
      const payload: Partial<Trail> = {
        ...editingTrail,
        slug: editingTrail.slug || slugify(editingTrail.name),
      };
      await saveTrail(payload);
      showToast(`✓ Trail "${payload.name}" saved!`);
      setIsModalOpen(false);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to save trail", "err");
    } finally {
      setSavingTrail(false);
    }
  };

  const handleDeleteTrail = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to delete trail "${name}"?`)) return;
    try {
      await deleteTrail(id);
      showToast(`✓ Deleted "${name}"`);
      setIsModalOpen(false);
      await loadData();
    } catch (err: any) {
      showToast(err.message || "Failed to delete trail", "err");
    }
  };

  // Stops are written as a full list — Library's set_stops replaces all stops
  // of the trail (delete-then-insert, NOT atomic), so every mutation sends the
  // complete desired array and must never be able to fail server-side:
  //   - curated_trail_stops.distillery_id is NOT NULL, so a stop may only be
  //     added with a resolved distillery — an unmatched name blocks the add
  //     instead of sending null (which would fail the insert AFTER the delete
  //     already wiped the trail's stops).
  //   - Mutations are refused while the current stop list is still loading,
  //     so a stale/partial list can never be written over the real one.
  const handleAddStop = async () => {
    if (!editingTrail?.id || !newDistilleryName.trim()) return;
    if (loadingStops) {
      showToast("Stops are still loading — try again in a moment", "err");
      return;
    }
    try {
      const match = await findDistilleryByName(newDistilleryName);
      if (!match) {
        showToast(`No distillery found matching "${newDistilleryName.trim()}" — add it to the distillery catalog first`, "err");
        return;
      }
      const desired = [
        ...stops.map((s) => ({ distillery_id: s.distillery_id, notes: s.notes })),
        { distillery_id: match.id, notes: newStopNotes.trim() || null },
      ];
      await setTrailStops(editingTrail.id, desired);
      setNewDistilleryName("");
      setNewStopNotes("");
      const stopRows = await listTrailStops(editingTrail.id);
      setStops(stopRows || []);
      showToast(`✓ Added stop (${match.name})`);
    } catch (err: any) {
      showToast(err.message || "Failed to add stop", "err");
    }
  };

  const handleDeleteStop = async (stopId: string) => {
    if (!editingTrail?.id) return;
    if (loadingStops) {
      showToast("Stops are still loading — try again in a moment", "err");
      return;
    }
    try {
      const desired = stops
        .filter((s) => s.id !== stopId)
        .map((s) => ({ distillery_id: s.distillery_id, notes: s.notes }));
      await setTrailStops(editingTrail.id, desired);
      const stopRows = await listTrailStops(editingTrail.id);
      setStops(stopRows || []);
      showToast("✓ Removed stop");
    } catch (err: any) {
      showToast(err.message || "Failed to remove stop", "err");
    }
  };

  return (
    <div style={styles.container}>
      <GuestStudioTabs activeTab="trails" badgeCounts={{ trails: trails.length }} />
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "err" ? "#E05040" : "var(--gold)" }}>
          {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Whiskey Trails &amp; Passports</h1>
          <p style={styles.subtitle}>
            Design curated tasting passports, passport stamps, and regional distillery trails for members.
          </p>
        </div>
        <button type="button" style={styles.btnPrimary} onClick={handleNewTrail}>
          + New Trail
        </button>
      </div>

      {/* Toolbar */}
      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder="Search trails by name, description, slug…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={styles.searchInput}
        />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Regions</option>
            <option value="kc_local">Kansas City Local</option>
            <option value="missouri">Missouri Spirits</option>
            <option value="regional">Midwest Regional</option>
            <option value="national">National Heritage</option>
            <option value="international">World Whiskies</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={styles.select}
          >
            <option value="all">All Statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {/* Trail Cards */}
      {loading ? (
        <div style={styles.loader}>Loading trails…</div>
      ) : filteredTrails.length === 0 ? (
        <div style={styles.emptyState}>No trails found matching criteria.</div>
      ) : (
        <div style={styles.trailGrid}>
          {filteredTrails.map((t) => (
            <div
              key={t.id}
              style={styles.trailCard}
              onClick={() => handleEditTrail(t)}
            >
              <div style={styles.cardHeader}>
                <span style={styles.regionBadge}>
                  {REGION_LABELS[t.region || ""] || "Whiskey Trail"}
                </span>
                <span
                  style={{
                    ...styles.statusBadge,
                    background:
                      t.status === "published"
                        ? "rgba(39,174,96,0.15)"
                        : "rgba(138,122,102,0.15)",
                    color: t.status === "published" ? "#27ae60" : "var(--muted)",
                  }}
                >
                  {(t.status || "unknown").toUpperCase()}
                </span>
              </div>

              <h3 style={styles.cardTitle}>{t.name}</h3>
              {t.description && <p style={styles.cardDesc}>{t.description}</p>}

              <div style={styles.metaRow}>
                <span>Duration: <strong>{t.duration || "Self-Paced"}</strong></span>
                <span>Stops: <strong>{t.stops_count || 0}</strong></span>
                {t.c33_exclusive && <span style={styles.c33Badge}>C33 VIP</span>}
              </div>

              <div style={styles.cardFooter}>
                <span>Slug: <code>{t.slug}</code></span>
                <span style={{ color: "var(--gold)", fontWeight: 700 }}>Edit Trail &amp; Stops →</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor Modal */}
      {isModalOpen && editingTrail && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingTrail.id ? `Edit: ${editingTrail.name}` : "New Whiskey Trail"}
              </h2>
              <button
                type="button"
                style={styles.btnIcon}
                onClick={() => setIsModalOpen(false)}
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveTrail} style={styles.modalForm}>
              <div className="station-form-grid" style={styles.formGrid}>
                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Trail Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Kentucky Bourbon Trail Master Class"
                    value={editingTrail.name || ""}
                    onChange={(e) => {
                      const name = e.target.value;
                      setEditingTrail({
                        ...editingTrail,
                        name,
                        slug: editingTrail.slug || slugify(name),
                      });
                    }}
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Slug *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. kentucky-bourbon-trail"
                    value={editingTrail.slug || ""}
                    onChange={(e) =>
                      setEditingTrail({ ...editingTrail, slug: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Region</label>
                  <select
                    value={editingTrail.region || "kc_local"}
                    onChange={(e) =>
                      setEditingTrail({ ...editingTrail, region: e.target.value })
                    }
                    style={styles.select}
                  >
                    <option value="kc_local">Kansas City Local</option>
                    <option value="missouri">Missouri Spirits</option>
                    <option value="regional">Midwest Regional</option>
                    <option value="national">National Heritage</option>
                    <option value="international">World Whiskies</option>
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Estimated Duration</label>
                  <input
                    type="text"
                    placeholder="e.g. 2 Days / Weekend"
                    value={editingTrail.duration || ""}
                    onChange={(e) =>
                      setEditingTrail({ ...editingTrail, duration: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Difficulty / Pace</label>
                  <select
                    value={editingTrail.difficulty || "easy"}
                    onChange={(e) =>
                      setEditingTrail({ ...editingTrail, difficulty: e.target.value })
                    }
                    style={styles.select}
                  >
                    <option value="easy">Easy (Day Trip / Flight)</option>
                    <option value="moderate">Moderate (Weekend)</option>
                    <option value="road_trip">Road Trip / Extended</option>
                  </select>
                </div>

                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Status</label>
                  <select
                    value={editingTrail.status || "draft"}
                    onChange={(e) =>
                      setEditingTrail({ ...editingTrail, status: e.target.value })
                    }
                    style={styles.select}
                  >
                    <option value="draft">Draft (Hidden)</option>
                    <option value="published">Published (Live in App)</option>
                  </select>
                </div>

                <div style={{ ...styles.fieldGroup, justifyContent: "center" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text)", cursor: "pointer", marginTop: 16 }}>
                    <input
                      type="checkbox"
                      checked={editingTrail.c33_exclusive || false}
                      onChange={(e) =>
                        setEditingTrail({
                          ...editingTrail,
                          c33_exclusive: e.target.checked,
                        })
                      }
                    />
                    C33 Member Exclusive Trail
                  </label>
                </div>

                <div style={{ ...styles.fieldGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Description</label>
                  <textarea
                    rows={3}
                    placeholder="Describe the experience, tasting checkpoints, and milestone reward..."
                    value={editingTrail.description || ""}
                    onChange={(e) =>
                      setEditingTrail({ ...editingTrail, description: e.target.value })
                    }
                    style={styles.textarea}
                  />
                </div>
              </div>

              {/* Stops Section */}
              {editingTrail.id && (
                <div style={styles.stopsSection}>
                  <h3 style={styles.stopsTitle}>Trail Stops &amp; Passport Sequence</h3>
                  {loadingStops ? (
                    <div style={{ fontSize: 12, color: "var(--muted)" }}>Loading stops…</div>
                  ) : stops.length === 0 ? (
                    <div style={{ fontSize: 12, color: "var(--muted)", fontStyle: "italic" }}>
                      No stops added to this trail yet.
                    </div>
                  ) : (
                    <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
                      {stops.map((s, idx) => (
                        <div key={s.id} style={styles.stopRow}>
                          <span style={styles.stopNum}>#{idx + 1}</span>
                          <div style={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
                            <strong>{s.distillery_name || "Distillery Checkpoint"}</strong>
                            {s.notes && <div style={{ fontSize: 11, color: "var(--muted)" }}>{s.notes}</div>}
                          </div>
                          <button
                            type="button"
                            style={styles.btnIconSmall}
                            onClick={() => handleDeleteStop(s.id)}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Stop Form */}
                  <div style={styles.addStopRow}>
                    <input
                      type="text"
                      placeholder="Add distillery or venue name…"
                      value={newDistilleryName}
                      onChange={(e) => setNewDistilleryName(e.target.value)}
                      style={{ ...styles.input, flex: "1 1 160px" }}
                    />
                    <input
                      type="text"
                      placeholder="Notes (e.g. Sample single barrel rye)…"
                      value={newStopNotes}
                      onChange={(e) => setNewStopNotes(e.target.value)}
                      style={{ ...styles.input, flex: "1 1 160px" }}
                    />
                    <button
                      type="button"
                      style={styles.btnSecondary}
                      onClick={handleAddStop}
                    >
                      + Add Stop
                    </button>
                  </div>
                </div>
              )}

              {/* Modal Actions */}
              <div style={styles.modalActions}>
                {editingTrail.id && (
                  <button
                    type="button"
                    style={styles.btnDanger}
                    onClick={() =>
                      handleDeleteTrail(editingTrail.id!, editingTrail.name || "Trail")
                    }
                  >
                    Delete Trail
                  </button>
                )}
                <div style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
                  <button
                    type="button"
                    style={styles.btnSecondary}
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" style={styles.btnPrimary} disabled={savingTrail}>
                    {savingTrail ? "Saving…" : "Save Trail"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: { padding: "20px 24px", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 700, margin: 0, color: "var(--text)" },
  subtitle: { fontSize: 13, color: "var(--muted)", margin: "4px 0 0" },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 },
  searchInput: { flex: 1, minWidth: 260, background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  select: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  // min(340px, 100%): on a phone the content box is ~343px, and a long name pushed the 340px card
  // past the screen edge; now one full-width column there, unchanged wherever 340px fits.
  trailGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(340px, 100%), 1fr))", gap: 16 },
  trailCard: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: 16, cursor: "pointer", display: "flex", flexDirection: "column", gap: 8 },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  regionBadge: { fontSize: 10, fontWeight: 800, color: "var(--gold)", textTransform: "uppercase", letterSpacing: 0.5 },
  statusBadge: { fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4 },
  cardTitle: { fontSize: 17, fontWeight: 700, margin: 0, color: "var(--text)" },
  cardDesc: { fontSize: 12, color: "var(--muted)", margin: 0, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" },
  metaRow: { display: "flex", gap: 12, alignItems: "center", fontSize: 11, color: "var(--muted)", marginTop: "auto" },
  c33Badge: { background: "rgba(155,89,182,0.15)", color: "#9B59B6", padding: "2px 6px", borderRadius: 4, fontWeight: 800, fontSize: 10 },
  cardFooter: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 10, marginTop: 4, fontSize: 11, color: "var(--muted)" },
  btnPrimary: { background: "var(--gold)", color: "#111", border: 0, padding: "8px 16px", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" },
  btnSecondary: { background: "transparent", color: "var(--text)", border: "1px solid var(--border)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" },
  btnDanger: { background: "transparent", color: "#E05040", border: "1px solid rgba(224,80,64,0.4)", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" },
  btnIcon: { background: "transparent", border: 0, color: "var(--muted)", fontSize: 22, cursor: "pointer" },
  btnIconSmall: { background: "transparent", border: 0, color: "#E05040", fontSize: 18, cursor: "pointer", padding: "0 6px" },
  loader: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 14 },
  emptyState: { padding: 40, textAlign: "center", color: "var(--muted)", fontSize: 13 },
  toast: { position: "fixed", bottom: 20, right: 20, color: "#111", fontWeight: 700, padding: "10px 18px", borderRadius: 8, zIndex: 9999, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.4)" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modalContent: { background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, width: "100%", maxWidth: 760, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--border)" },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "var(--text)", margin: 0 },
  modalForm: { padding: 20, overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 16 },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 },
  fieldGroup: { display: "flex", flexDirection: "column", gap: 4 },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--muted)", letterSpacing: 0.5 },
  input: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "8px 12px", fontSize: 13 },
  textarea: { width: "100%", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", padding: "10px 12px", fontSize: 13, resize: "vertical" },
  stopsSection: { background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 10, padding: 14 },
  stopsTitle: { fontSize: 13, fontWeight: 700, color: "var(--gold)", textTransform: "uppercase", letterSpacing: 0.5, margin: "0 0 10px" },
  stopRow: { display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", background: "var(--surface)", borderRadius: 6, border: "1px solid var(--border)" },
  stopNum: { fontSize: 11, fontWeight: 800, color: "var(--gold)", width: 24 },
  // wrap: two text inputs + Add don't fit one phone-width line; inputs take a full row each there.
  addStopRow: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 8 },
  modalActions: { display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid var(--border)", paddingTop: 16 },
};
